import { useEffect, useState } from 'react'
import { StorePart, StorePartDetailsQuery } from 'graphql'

type UseStorePartAutoPopulateParams = {
	called: boolean
	storePartLoading: boolean
	storePartData: StorePartDetailsQuery | undefined
	selectedProductId?: number
	selectedPart?: StorePart
	onChange: ( id?: number, option?: StorePart ) => void
	onNotFound?: ( productId: number ) => void
}

/**
 * Manages auto-population logic for StorePartPicker
 * - Calls onChange when a StorePart is successfully fetched
 * - Calls onNotFound when a StorePart query completes but finds nothing
 * - Ensures callbacks are only called once per selectedProductId
 */
export function useStorePartAutoPopulate( {
	called,
	storePartLoading,
	storePartData,
	selectedProductId,
	selectedPart,
	onChange,
	onNotFound,
}: UseStorePartAutoPopulateParams ): void {
	const [ hasCalledOnChange, setHasCalledOnChange ] = useState( false )

	useEffect( () => {
		// Reset the flag when selectedProductId changes
		setHasCalledOnChange( false )
	}, [ selectedProductId ] )

	useEffect( () => {
		// Only check for not found if the query has actually been called and completed
		if ( called && !storePartLoading && selectedProductId && !storePartData?.storePart && onNotFound && !hasCalledOnChange ) {
			onNotFound( selectedProductId )
			setHasCalledOnChange( true )
		}
	}, [ called, storePartLoading, storePartData, selectedProductId, onNotFound, hasCalledOnChange ] )

	useEffect( () => {
		// If StorePart was fetched successfully and we don't have a selectedPart, call onChange to populate the form
		if ( !storePartLoading && storePartData?.storePart && !selectedPart && !hasCalledOnChange ) {
			const fetchedStorePart = storePartData.storePart as StorePart
			onChange( fetchedStorePart.id, fetchedStorePart )
			setHasCalledOnChange( true )
		}
	}, [ storePartLoading, storePartData, selectedPart, onChange, hasCalledOnChange ] )
}
